import React, { useEffect, useState, useCallback, useRef } from "react";
import "./../../../../assets/css/purchase/form.css";

import InitialDataFormLayout from "../components/InitialDataFormLayout";
import {
  labelStyle,
  inputStyle,
  selectStyle,
  sectionHeaderStyle,
  sectionIconStyle,
  sectionTitleStyle,
} from "../components/formStyles";

import {
  deleteData,
  getAlert,
  insertData,
  parseCurrency,
  updateData,
} from "../../../../utils/SamuiUtils";

const DEFAULT_FORM = {
  comp_id: parseCurrency(window.localStorage.getItem("company"), 10),
  Colour_Id: 0,
  Colour_Code: "",
  Colour_Name: "",
  Colour_Status: "Y",
};

const ColourForm = ({
  callInitialize,
  mode,
  name,
  formData,
  setFormData,
  selectedData,
}) => {
  const [initialFormData, setInitialFormData] = useState(DEFAULT_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const isSavingRef = useRef(false);

  useEffect(() => {
    if (mode === "I") {
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } else if (mode === "U" && selectedData) {
      const editData = {
        comp_id: parseCurrency(selectedData.Comp_Id, 10),
        Colour_Id: parseCurrency(selectedData.Colour_Id, 10),
        Colour_Code: selectedData.Colour_Code || "",
        Colour_Name: selectedData.Colour_Name || "",
        Colour_Status: selectedData.Colour_Status || "Y",
      };
      setFormData(editData);
      setInitialFormData(editData);
    }
  }, [mode, selectedData, setFormData]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleInsert = useCallback(async () => {
    try {
      const { Colour_Id, comp_id, ...dataToInsert } = formData;
      const fields = "Colour_Code,Colour_Name,Colour_Status,Comp_Id";
      
      // ตรวจสอบว่ามีค่าที่จำเป็นหรือไม่
      if (!dataToInsert.Colour_Code || !dataToInsert.Colour_Name) {
        getAlert("WARNING", "โปรดกรอกรหัสสีและชื่อสี");
        return;
      }
      
      // แปลง Colour_Code เป็นตัวพิมพ์ใหญ่ และส่งเป็น array ธรรมดา (ไม่ใช่ array of arrays)
      const colourCode = String(dataToInsert.Colour_Code || "").toUpperCase().trim();
      const colourName = String(dataToInsert.Colour_Name || "").trim();
      const colourStatus = String(dataToInsert.Colour_Status || "Y");
      const compId = Number(comp_id) || Number(window.localStorage.getItem("company")) || 0;
      
      // ตรวจสอบว่าค่าไม่ว่าง
      if (!colourCode || colourCode.length === 0) {
        getAlert("WARNING", "โปรดกรอกรหัสสี");
        return;
      }
      if (!colourName || colourName.length === 0) {
        getAlert("WARNING", "โปรดกรอกชื่อสี");
        return;
      }
      if (!compId || compId === 0) {
        getAlert("WARNING", "ไม่พบ Company ID");
        return;
      }
      
      const values = [
        colourCode,
        colourName,
        colourStatus,
        compId,
      ];
      
      // Debug: ตรวจสอบ fields และ values
      console.log("Insert Colour - Fields:", fields);
      console.log("Insert Colour - Values:", values);
      console.log("Insert Colour - Fields count:", fields.split(", ").length);
      console.log("Insert Colour - Values count:", values.length);
      
      await insertData("Tb_Set_ItmColour", fields, values);
      await callInitialize();
      setFormData(DEFAULT_FORM);
      setInitialFormData(DEFAULT_FORM);
    } catch (error) {
      console.error("Error inserting data:", error);
      getAlert(
        "FAILED",
        "ไม่สามารถบันทึกข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize, setFormData]);

  const handleUpdate = useCallback(async () => {
    try {
      const where = { Colour_Id: formData.Colour_Id };
      const { Colour_Id, comp_id, ...values } = formData;
      // แปลง Colour_Code เป็นตัวพิมพ์ใหญ่
      const updateValues = {
        ...values,
        Colour_Code: String(values.Colour_Code || "").toUpperCase().trim(),
        Comp_Id: comp_id,
      };
      const res = await updateData("Tb_Set_ItmColour", updateValues, where);
      if (res) {
        await callInitialize();
      }
    } catch (error) {
      getAlert(
        "FAILED",
        "ไม่สามารถอัปเดตข้อมูลได้ เนื่องจากมีข้อมูลนี้อยู่ในระบบแล้ว"
      );
    }
  }, [formData, callInitialize]);

  const handleDelete = async () => {
    if (!selectedData?.Colour_Id) {
      getAlert("WARNING", "ไม่พบข้อมูลที่ต้องการลบ");
      return;
    }

    getAlert("C_DIALOG", "ยืนยันการลบข้อมูล", async (isConfirm) => {
      if (isConfirm) {
        await deleteData(
          "Tb_Set_ItmColour",
          `WHERE Colour_Id = ${selectedData.Colour_Id}`
        );
        await callInitialize();
      }
    });
  };

  const handleSubmit = useCallback(async () => {
    if (isSavingRef.current) return;
    if (!formData.Colour_Code || !formData.Colour_Name) {
      getAlert("WARNING", "โปรดกรอกรหัสสีและชื่อสี");
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    try {
      if (mode === "I") {
        await handleInsert();
      } else if (mode === "U") {
        await handleUpdate();
      }
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  }, [formData.Colour_Code, formData.Colour_Name, mode, handleInsert, handleUpdate]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (
        event.target.tagName === "INPUT" ||
        event.target.tagName === "TEXTAREA" ||
        event.target.tagName === "SELECT"
      ) {
        return;
      }

      if (event.key === "Escape") {
        event.preventDefault();
        getAlert("C_DIALOG", "ต้องการยกเลิกการทำงานหรือไม่?", (isConfirm) => {
          if (isConfirm) {
            callInitialize();
          }
        });
      }

      if (event.key === "F10") {
        event.preventDefault();
        if (!isSavingRef.current) handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleSubmit, callInitialize]);

  const handleReset = () => {
    setFormData(initialFormData);
  };

  const handleBack = () => {
    callInitialize();
  };

  return (
    <InitialDataFormLayout
      name={name}
      entityPath="/tbsetcolour"
      mode={mode}
      onSubmit={handleSubmit}
      isSubmittingDisabled={isSaving}
      onBack={handleBack}
      onReset={handleReset}
      onDelete={mode === "U" ? handleDelete : undefined}
      callInitialize={callInitialize}
    >
      <div style={sectionHeaderStyle}>
        <i className="fas fa-palette" style={sectionIconStyle}></i>
        <h5 style={sectionTitleStyle}>ข้อมูลสี</h5>
      </div>
      <div className="row g-4">
        <div className="col-md-4">
          <label style={labelStyle}>รหัสสี (ภาษาอังกฤษ)</label>
          <input
            type="text"
            className="form-control"
            name="Colour_Code"
            value={formData.Colour_Code || ""}
            onChange={handleChange}
            style={inputStyle}
            placeholder="เช่น RED, BLU, GRN"
            onInput={(e) => {
              e.target.value = e.target.value.toUpperCase();
            }}
          />
          <small style={{ color: "#E13F57", fontSize: "12px" }}>
            ⚠️มีผลกับการสร้างรหัสสินค้า
          </small>
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>ชื่อสี</label>
          <input
            type="text"
            className="form-control"
            name="Colour_Name"
            value={formData.Colour_Name || ""}
            onChange={handleChange}
            style={inputStyle}
            placeholder="เช่น แดง, น้ำเงิน, เขียว"
          />
        </div>
        <div className="col-md-4">
          <label style={labelStyle}>สถานะ</label>
          <select
            className="form-select"
            name="Colour_Status"
            value={formData.Colour_Status || "Y"}
            onChange={handleChange}
            style={selectStyle}
          >
            <option value="Y">ใช้งาน</option>
            <option value="N">ไม่ใช้งาน</option>
          </select>
        </div>
      </div>
    </InitialDataFormLayout>
  );
};

export default ColourForm;
